import { useMemo } from "react";

import Icons from "assets";

import Card, { CardBody, CardHeader, CardRow } from "components/partials/card/card";
import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { Typography } from "ui/typography";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import type { LandingPageAttributes } from "hooks/api/use-touchpoint-versions";

import { convertTouchpointToUrl } from "utilities";

import { themeColors } from "design-tokens";

import { getCampaignPaths } from "utilities/app-routes";

import { useRedirectTouchpointUrl } from "containers/admin/clients/touchpoint/tab-components/details-tab/hooks/use-redirect-touchpoint-url";
import { getVersionStatusTexts } from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab.helper";
import { TouchpointCardProps } from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab.types";
import {
  StringToCopyableLink,
  getRedirectDestinationValue,
} from "containers/admin/clients/touchpoint/tab-components/details-tab/components/lp-version-content-settings-card.helper";

const InvertedBooleanStatus = (isOff = false) => (
  <Stack direction="row" spacing="0.5rem" sx={{ alignItems: "center" }}>
    <Typography variant="inherit" component="span">
      {isOff ? "OFF" : "ON"}
    </Typography>
    <Box
      aria-hidden
      sx={{
        width: "1rem",
        height: "1rem",
        borderRadius: "9999px",
        flexShrink: 0,
        backgroundColor: isOff ? themeColors.error[100] : themeColors.success[100],
      }}
    />
  </Stack>
);

const LPVersionContentSettingsCard = ({ selectedVersion, ...rest }: TouchpointCardProps) => {
  const currentCampaign = useCurrentCampaign();
  const currentClient = useCurrentClient();

  const { campaignSettingsEditPath } = useMemo(
    () => getCampaignPaths({ clientId: currentClient.id, campaignId: currentCampaign.id }),
    [currentClient.id, currentCampaign.id],
  );

  const selectedLP = selectedVersion as LandingPageAttributes | undefined;
  const { redirectTouchpointUrl, redirectTouchpointNoPublishedVersion } =
    useRedirectTouchpointUrl(selectedVersion);

  const isMissingCdnProfile = !currentCampaign?.cdnSubdirectoryName?.trim();
  if (!selectedVersion) {
    return null;
  }

  return (
    <Card {...rest}>
      <CardHeader heading="Version content settings" {...getVersionStatusTexts(selectedVersion)} />
      <CardBody>
        <CardRow title="Page Title" value={selectedLP?.pageTitle} />
        <CardRow
          title="Page URL"
          value={StringToCopyableLink(
            convertTouchpointToUrl(currentClient, currentCampaign, selectedLP?.url),
          )}
        />
        {isMissingCdnProfile && (
          <p className="flex items-center text-sm text-critical-700 -mt-2 mb-2">
            <Icons.WarningIndicator className="mr-1 size-5 shrink-0 p-0.5" />
            <a
              href={campaignSettingsEditPath}
              className="underline font-medium hover:no-underline focus:outline-none focus:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Please set the Campaign CDN Profile.
            </a>
          </p>
        )}
        <CardRow title="Page description" value={selectedLP?.pageDescription} />
        <CardRow
          title="User data prefill"
          value={InvertedBooleanStatus(selectedLP?.disablePrefill)}
        />
        <CardRow
          title="Redirect Destination"
          value={getRedirectDestinationValue({
            selectedLP,
            redirectTouchpointUrl,
            redirectTouchpointNoPublishedVersion,
          })}
        />
      </CardBody>
    </Card>
  );
};

export default LPVersionContentSettingsCard;
